import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import { ContextShape } from '@ardoq/data-model';
import {
  ContextSort,
  NumericSortOrder,
  SortAttribute,
  compareComponents,
} from '@ardoq/common-helpers';
import { Locale } from '@ardoq/locale';
import { componentOperations } from '@ardoq/core';
import type { ComponentsState } from 'streams/components/components$';

const sortFlatComponents = (
  components: APIComponentAttributes[],
  sort: ContextSort,
  locale: Locale
): APIComponentAttributes[] => {
  if (
    sort.attr === SortAttribute.ORDER ||
    sort.order === NumericSortOrder.ON_REFERENCE_DESCENDING ||
    sort.order === NumericSortOrder.ON_REFERENCE_ASCENDING
  ) {
    return components;
  }
  return [...components].sort((a, b) => compareComponents(a, b, sort, locale));
};

export const getContextComponentChildren = (
  context: ContextShape,
  components: ComponentsState
): APIComponentAttributes[] =>
  context.componentId
    ? (components.childrenByParent.get(context.componentId) ?? [])
    : [];

/**
 * Get components to be included in this view
 *
 * RelevantComponents in this case is calculated in the following way:
 * 1) If we're looking at a component
 *    a) If the component has children, return its children plus the context component
 *    b) Otherwise return the context component alone
 * 2) If we're looking at a workspace
 *    a) Return the flattened workspace hierarchy, sorted
 *
 * `hasComponentsAvailable` indicates if there were components available before
 * filtering.
 */
export const relevantComponentsFromContextShape = ({
  context,
  components,
  locale,
  includedComponentIds,
}: {
  context: ContextShape;
  components: ComponentsState;
  locale: Locale;
  includedComponentIds: ReadonlySet<ArdoqId>;
}): {
  relevantComponents: APIComponentAttributes[];
  hasComponentsAvailable: boolean;
} => {
  if (context.componentId) {
    const contextComponent = components.byId[context.componentId];
    if (!contextComponent) {
      return { relevantComponents: [], hasComponentsAvailable: false };
    }
    const filteredChildren = getContextComponentChildren(
      context,
      components
    ).filter(({ _id }) => includedComponentIds.has(_id));
    return {
      relevantComponents:
        filteredChildren.length > 0
          ? [contextComponent, ...filteredChildren]
          : [contextComponent],
      hasComponentsAvailable: true,
    };
  }

  if (context.workspaceId) {
    const roots = components.models.filter(
      component =>
        !component.parent && component.rootWorkspace === context.workspaceId
    );
    const flat = componentOperations.getFlatHierarchy(
      roots,
      components.childrenByParent
    );
    const relevantComponents = sortFlatComponents(flat, context.sort, locale);
    return {
      hasComponentsAvailable: relevantComponents.length > 0,
      relevantComponents,
    };
  }

  return { relevantComponents: [], hasComponentsAvailable: false };
};
